import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Images, Youtube, X, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to normalize gallery image URLs to current backend
const normalizeImageUrl = (url) => {
  if (!url) return url;
  
  // If it's a gallery image URL, ensure it uses the current API_URL
  if (url.includes('/api/cards/gallery/images/')) {
    const imageId = url.split('/api/cards/gallery/images/')[1]?.split('?')[0];
    if (imageId) {
      return `${API_URL}/api/cards/gallery/images/${imageId}`;
    }
  }
  
  return url;
};

// Gallery Display Component
export const BioGalleryDisplay = ({ images = [], className = "", isOwner = false, onDeleteImage }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Normalize all image URLs - do this before hooks
  const normalizedImages = (images || []).map(normalizeImageUrl);
  const imageCount = normalizedImages.length;

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goNext = useCallback(() => {
    if (imageCount > 0) {
      setCurrentIndex((prev) => (prev + 1) % imageCount);
    }
  }, [imageCount]);

  const goPrev = useCallback(() => {
    if (imageCount > 0) {
      setCurrentIndex((prev) => (prev - 1 + imageCount) % imageCount);
    }
  }, [imageCount]);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxOpen, goNext, goPrev, closeLightbox]);

  // Swipe handlers for lightbox
  const handleLightboxTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleLightboxTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleLightboxTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        goNext();
      } else {
        goPrev();
      }
    }
  };

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const handleDeleteImage = (index, e) => {
    e.stopPropagation();
    if (onDeleteImage) {
      onDeleteImage(index);
    }
  };

  // Early return after all hooks
  if (imageCount === 0) return null;

  return (
    <>
      <div className={`bio-gallery ${className}`}>
        <div className="flex items-center gap-2 mb-3">
          <Images className="w-4 h-4 text-purple-600" />
          <h3 className="text-base font-bold text-gray-900">Gallery</h3>
        </div>
        
        {/* 2x2 Compact Grid - All images visible on screen */}
        <div className="grid grid-cols-2 gap-1.5 max-w-sm mx-auto">
          {normalizedImages.slice(0, 4).map((imageUrl, index) => (
            <div
              key={index}
              className="relative rounded-lg overflow-hidden cursor-pointer group shadow-md hover:shadow-lg transition-all"
              style={{ aspectRatio: '1/1' }}
              onClick={() => openLightbox(index)}
            >
              <img
                src={imageUrl}
                alt={`Gallery ${index + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 pointer-events-none"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none" />
              
              {/* Delete button for owners */}
              {isOwner && (
                <button
                  onClick={(e) => handleDeleteImage(index, e)}
                  className="absolute top-1.5 right-1.5 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  title="Delete image"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal - Using Portal for true fullscreen */}
      {lightboxOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black flex items-center justify-center"
          style={{ zIndex: 99999 }}
          onClick={closeLightbox}
          onTouchStart={handleLightboxTouchStart}
          onTouchMove={handleLightboxTouchMove}
          onTouchEnd={handleLightboxTouchEnd}
        >
          {/* Close Button */}
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-4 right-4 text-white hover:text-gray-300 bg-white/20 hover:bg-white/30 rounded-full p-3 transition-all backdrop-blur-sm"
            style={{ zIndex: 100000 }}
          >
            <X className="w-8 h-8" />
          </button>
          
          {/* Navigation Buttons */}
          {imageCount > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 text-white hover:text-white bg-white/20 hover:bg-white/40 rounded-full p-4 sm:p-5 transition-all backdrop-blur-sm hover:scale-110"
                style={{ zIndex: 100000 }}
              >
                <ChevronLeft className="w-10 h-10 sm:w-12 sm:h-12" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 text-white hover:text-white bg-white/20 hover:bg-white/40 rounded-full p-4 sm:p-5 transition-all backdrop-blur-sm hover:scale-110"
                style={{ zIndex: 100000 }}
              >
                <ChevronRight className="w-10 h-10 sm:w-12 sm:h-12" />
              </button>
            </>
          )}
          
          {/* Image - Maximum fullscreen with padding for buttons */}
          <img
            src={normalizedImages[currentIndex]}
            alt={`Gallery ${currentIndex + 1}`}
            className="max-h-[90vh] max-w-[85vw] w-auto h-auto object-contain select-none"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
          
          {/* Image Counter */}
          <div 
            className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white text-lg font-medium bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full"
            style={{ zIndex: 100000 }}
          >
            {currentIndex + 1} / {imageCount}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// YouTube Display Component
export const BioYouTubeDisplay = ({ videos = [], className = "", isOwner = false, onDeleteVideo }) => {
  const [activeVideo, setActiveVideo] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);

  if (!videos || videos.length === 0) return null;

  // Extract video ID from URL or return as-is if already an ID
  const extractVideoId = (input) => {
    if (!input) return '';
    const trimmed = input.trim();
    
    // If it's already just a video ID (11 characters)
    if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
      return trimmed;
    }
    
    // Try to extract from various YouTube URL formats
    const match = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
    if (match && match[1]) {
      return match[1];
    }
    
    // Try to find any 11-character ID in the string
    const idMatch = trimmed.match(/[a-zA-Z0-9_-]{11}/);
    if (idMatch) {
      return idMatch[0];
    }
    
    return trimmed;
  };

  const getThumbnailUrl = (videoInput) => {
    const videoId = extractVideoId(videoInput);
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  };

  const getEmbedUrl = (videoInput) => {
    const videoId = extractVideoId(videoInput);
    // No autoplay - user clicks play button to start video
    return `https://www.youtube.com/embed/${videoId}?rel=0`;
  };

  const handleVideoSelect = (index) => {
    if (index !== activeVideo) {
      setActiveVideo(index);
    }
  };

  const handleUnmute = () => {
    setIsMuted(false);
    setHasInteracted(true);
  };

  const handleDeleteVideo = (index, e) => {
    e.stopPropagation();
    if (onDeleteVideo) {
      onDeleteVideo(index);
      // If deleting active video, reset to first video
      if (index === activeVideo && videos.length > 1) {
        setActiveVideo(0);
      }
    }
  };

  return (
    <div className={`bio-youtube ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Youtube className="w-4 h-4 text-red-600" />
        <h3 className="text-base font-bold text-gray-900">Videos</h3>
      </div>
      
      {/* Compact Video Player - fits on screen */}
      <div className="max-w-sm mx-auto">
        <div className="relative aspect-video rounded-lg overflow-hidden shadow-md mb-2 group">
          <iframe
            key={`video-${activeVideo}-${videos[activeVideo]}-${isMuted}`}
            src={getEmbedUrl(videos[activeVideo])}
            title="YouTube video"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          
          {/* Tap for Sound button - positioned at top-left to avoid YouTube controls */}
          {isMuted && !hasInteracted && (
            <button
              onClick={handleUnmute}
              className="absolute top-2 left-2 z-50 flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-full text-xs font-semibold transition-all shadow-xl animate-bounce hover:animate-none"
              style={{ pointerEvents: 'auto' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <line x1="23" y1="9" x2="17" y2="15"></line>
                <line x1="17" y1="9" x2="23" y2="15"></line>
              </svg>
              Sound
            </button>
          )}
          
          {/* Delete button for owner - only on main video when single video */}
          {isOwner && videos.length === 1 && (
            <button
              onClick={(e) => handleDeleteVideo(0, e)}
              className="absolute top-2 right-2 z-50 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              title="Delete video"
              style={{ pointerEvents: 'auto' }}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Compact Thumbnail Strip (if more than 1 video) */}
        {videos.length > 1 && (
          <div className="grid grid-cols-4 gap-1.5">
            {videos.map((videoId, index) => (
              <div
                key={videoId}
                className={`relative aspect-video rounded overflow-hidden cursor-pointer transition-all group/thumb ${
                  activeVideo === index 
                    ? 'ring-2 ring-red-500 ring-offset-1' 
                    : 'hover:opacity-80'
                }`}
                onClick={() => handleVideoSelect(index)}
              >
                <img
                  src={getThumbnailUrl(videoId)}
                  alt={`Video ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {activeVideo !== index && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-5 h-5 bg-red-600/90 rounded-full flex items-center justify-center">
                      <Youtube className="w-3 h-3 text-white ml-0.5" />
                    </div>
                  </div>
                )}
                {/* Delete button for owner on thumbnails */}
                {isOwner && (
                  <button
                    onClick={(e) => handleDeleteVideo(index, e)}
                    className="absolute top-0.5 right-0.5 z-10 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full shadow-lg opacity-0 group-hover/thumb:opacity-100 transition-opacity"
                    title="Delete video"
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Rich Bio Display Component
export const BioRichTextDisplay = ({ content, className = "" }) => {
  if (!content || content === '<p><br></p>' || content.replace(/<[^>]*>/g, '').trim() === '') {
    return null;
  }

  return (
    <div 
      className={`bio-rich-text prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: content }}
      style={{
        // Override prose defaults for better integration
        '--tw-prose-body': '#374151',
        '--tw-prose-headings': '#111827',
        '--tw-prose-links': '#d97706',
      }}
    />
  );
};

export default { BioGalleryDisplay, BioYouTubeDisplay, BioRichTextDisplay };
